import { useRef, useState, useEffect } from 'react'
import { MapboxMapManager } from '../../map/MapboxMapManager'
import { RouteRenderer } from '../../map/RouteRenderer'
import { POIRenderer } from '../../map/POIRenderer'
import { PhotoRenderer } from '../../map/PhotoRenderer'
import { CommentRenderer } from '../../../utils/commentRenderer'
import { UserPOIRenderer } from '../../../utils/userPoiRenderer'
import { clearAllHoverPanels } from '../../../utils/hoverPanelCleanup'
import { POI, Photo } from '@cya-routes/shared'
import { Comment } from '../../../types/comments'
import { UserPOI } from '../../../types/userPois'

interface UseMapLifecycleProps {
  onPOISelect: (poi: POI) => void
  onPhotoSelect: (photo: Photo) => void
  onCommentSelect?: (comment: Comment) => void
  onUserPOISelect?: (poi: UserPOI) => void
  is3DEnabled: boolean
  disableNavigation?: boolean
  embedMode?: boolean
}

interface UseMapLifecycleReturn {
  mapContainer: React.RefObject<HTMLDivElement>
  mapManager: React.MutableRefObject<MapboxMapManager | null>
  routeRenderer: React.MutableRefObject<RouteRenderer | null>
  poiRenderer: React.MutableRefObject<POIRenderer | null>
  photoRenderer: React.MutableRefObject<PhotoRenderer | null>
  commentRenderer: React.MutableRefObject<CommentRenderer | null>
  userPoiRenderer: React.MutableRefObject<UserPOIRenderer | null>
  renderersInitialized: boolean
}

/**
 * Custom hook that manages the lifecycle of the map and its renderers
 * Handles initialization, setup, and cleanup of map-related resources
 */
export function useMapLifecycle({
  onPOISelect,
  onPhotoSelect,
  onCommentSelect,
  onUserPOISelect,
  is3DEnabled,
  disableNavigation = false,
  embedMode = false
}: UseMapLifecycleProps): UseMapLifecycleReturn {
  // Map and renderer refs
  const mapContainer = useRef<HTMLDivElement>(null)
  const mapManager = useRef<MapboxMapManager | null>(null)
  const routeRenderer = useRef<RouteRenderer | null>(null)
  const poiRenderer = useRef<POIRenderer | null>(null)
  const photoRenderer = useRef<PhotoRenderer | null>(null)
  const commentRenderer = useRef<CommentRenderer | null>(null)
  const userPoiRenderer = useRef<UserPOIRenderer | null>(null)
  
  // Track initialization state
  const [renderersInitialized, setRenderersInitialized] = useState(false)

  // Initialize map and renderers
  useEffect(() => {
    if (!mapContainer.current) return

    const initializeMap = async () => {
      try {
        // Initialize map manager with optional navigation disable and embed mode
        mapManager.current = new MapboxMapManager(mapContainer.current!, { disableNavigation, embedMode })
        
        // Initialize renderers when map is loaded
        const map = mapManager.current.getMap()
        if (map) {
          const setupRenderers = () => {
            routeRenderer.current = new RouteRenderer(map)
            // Pass undefined for onPOIUpdate to disable dragging
            poiRenderer.current = new POIRenderer(map, undefined, onPOISelect)
            // Pass undefined for onPhotoUpdate to disable dragging
            photoRenderer.current = new PhotoRenderer(map, onPhotoSelect, undefined)
            // Initialize comment renderer
            commentRenderer.current = new CommentRenderer(map)
            if (onCommentSelect) {
              commentRenderer.current.setOnCommentClick(onCommentSelect)
            }
            // Initialize user POI renderer
            userPoiRenderer.current = new UserPOIRenderer(map)
            if (onUserPOISelect) {
              userPoiRenderer.current.setOnPOIClick(onUserPOISelect)
            }
            
            // Add terrain for 3D
            try {
              if (!map.getSource('mapbox-dem')) {
                map.addSource('mapbox-dem', {
                  type: 'raster-dem',
                  url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
                  tileSize: 512,
                  maxzoom: 14
                })
                if (is3DEnabled) {
                  map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 })
                  map.setPitch(30)
                }
              }
            } catch (error) {
              console.log('3D terrain not available')
            }
            
            setRenderersInitialized(true)
          }

          if (map.loaded()) {
            setupRenderers()
          } else {
            map.on('load', setupRenderers)
          }
        }
      } catch (error) {
        console.error('Error initializing map:', error)
      }
    }

    initializeMap()

    // Cleanup function
    return () => {
      try {
        // Clean up any orphaned hover panels
        clearAllHoverPanels()
        
        // Clean up renderers first (in reverse order of creation)
        userPoiRenderer.current?.clearMarkers()
        commentRenderer.current?.destroy()
        photoRenderer.current?.destroy()
        poiRenderer.current?.destroy()
        routeRenderer.current?.destroy()
        
        // Clean up map manager last
        mapManager.current?.destroy()
      } catch (error) {
        console.warn('Error during map lifecycle cleanup:', error)
      }
    }
  }, []) // Only run once on mount/unmount

  // Handle 3D terrain changes
  useEffect(() => {
    if (!mapManager.current || !renderersInitialized) return
    
    const map = mapManager.current.getMap()
    if (!map || !map.loaded()) return

    try {
      if (is3DEnabled && map.getSource('mapbox-dem')) {
        map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 })
        map.setPitch(30)
      } else if (!is3DEnabled && map.getTerrain()) {
        map.setTerrain(null)
        map.setPitch(0)
      }
    } catch (error) {
      console.log('Error updating 3D terrain:', error)
    }
  }, [is3DEnabled, renderersInitialized])

  return {
    mapContainer,
    mapManager,
    routeRenderer,
    poiRenderer,
    photoRenderer,
    commentRenderer,
    userPoiRenderer,
    renderersInitialized
  }
}